// apps/main/client/src/lib/anonFetch.js — RUN TU. ONE PLACE THAT DECIDES WHAT AN ANONYMOUS
// REQUEST CARRIES, USED BY BOTH ANONYMOUS PATHS.
//
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 THE SHAPE THIS FIXES, WHICH IS OLDER THAN TURNSTILE.
//
// RUN AB gave the homepage widget a device fingerprint and sent it as `x-cc-fp`; it is the PRIMARY
// anonymous identity server-side (`anonTrial.identity()`: fingerprint → cookie → IP), and both the
// per-visitor trial and the per-identity share of the spend pool are keyed on it. That header was
// added inside `widgetFetch` in `src/clark-widget/index.jsx` — the only anonymous send path there
// was at the time.
//
// RUN CH3 then MOVED the anonymous turn. The widget became a launcher that makes the first call and
// hands off; /clark makes every subsequent one, through `main.jsx`'s `authedFetch`, which knows
// nothing about `x-cc-fp`. MEASURED at this run's base commit (aa07a752): `x-cc-fp` appears in
// exactly one client file, and it is the widget's. So a stranger's first turn was identified by
// FINGERPRINT and their second by COOKIE-OR-IP — two identities for one visitor, which is a
// silently wrong denominator for both the trial and the spend share.
//
// This module is the fix and the pattern: the anonymous request's headers are decided ONCE, here,
// and both wrappers ask for them. Turnstile is added to the same place for the same reason — a
// second copy of "what an anonymous call carries" is how the first one drifted.
//
// ── 🔴 IT ALSO OWNS THE ONE REFUSAL THE SERVER CANNOT ISSUE ────────────────────────────────────
//
// Adam's ruling for an unreachable Cloudflare is FAIL CLOSED, and `turnstile.js` already fails
// closed on its own side of the call. But there is a state the server can never see: the BROWSER
// could not reach Cloudflare, so no token exists to send. Sending the request anyway would reach
// the server as `missing_token` and be refused there — correctly, but with the wrong words ("that
// security check didn't go through", which blames the visitor) and after touching a money endpoint.
//
// So this module refuses it here, before the request is made, and returns a SYNTHETIC 429 in the
// server's own refusal shape. Three things follow, and all three are the point:
//
//   · The visitor reads the degraded sentence — "that's on our side, not yours" — which is true.
//   · 🔴 THE TRIAL CANNOT HAVE BEEN SPENT, and not as a matter of careful bookkeeping: the request
//     never left the browser, so `trial.spend()` was never reached by anything. `clarkMeter`'s
//     structural guarantee (Turnstile is gate 1, the trial is gate 3) is preserved a step earlier.
//   · The composer stays live. Both call sites already branch on `verification: true` and
//     deliberately do NOT set their spent flag for it (widget `applyRefusal`; Clark
//     `setAnonRefusal({kind:"verification"})`), so NEITHER of them needed editing to get this
//     right — the body is shaped so their existing correct handling fires.
//
// `error` is `clark_anon_unavailable` rather than `clark_anon_verification` because those two are
// different facts and RUN CH3 already teaches /clark to tell them apart. Both leave the composer
// live; only this one means "we could not verify", and a log that conflates them cannot tell a
// Cloudflare incident from a wave of failed bot checks.
//
// ── WHY THE TOKEN IS ATTACHED PER-ENDPOINT AND NOT TO EVERY CALL ───────────────────────────────
// A Turnstile token is single-use. Attaching one to `GET /api/usage` burns a challenge on a request
// no gate reads. `ANON_GATED` is the exact set of endpoints `clarkMeter.checkClarkCap()` can reach
// anonymously — the two mounted with `clerkPopulateAuth` rather than `clerkAuthMiddleware`. If a
// third anonymous door is ever opened, it belongs in this list and in nothing else.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

import { getFingerprint } from "../../../src/clark-widget/fingerprint.js";
import { getTurnstileToken } from "./turnstileClient.js";

/**
 * The endpoints an anonymous visitor can actually reach that are gated by `verifyTurnstile()`.
 * Verified at aa07a752 by reading the route mounts: `/api/clark` (routes/clark.js) and
 * `/api/clark/answer/stream` (routes/clark-answer.js) take `clerkPopulateAuth`; `/api/clark/answer`
 * and `/api/clark/grounded` take `clerkAuthMiddleware` and are signed-in only.
 */
export const ANON_GATED = ["/api/clark", "/api/clark/answer/stream"];

export function isAnonGatedUrl(url) {
  const u = String(url || "").split("?")[0];
  return ANON_GATED.includes(u);
}

/** The exact body `turnstile.js`'s `turnstileRefusal({state:"unreachable"})` would have produced. */
export function turnstileUnavailableBody() {
  return {
    error: "clark_anon_unavailable",
    capped: true, verification: true, degraded: true, variant: "retry",
    tier: "anon", limit: null, used: null, remaining: null,
    citationPayload: null, resolvedSchema: null,
    title: "We couldn't verify that",
    reply: "We couldn't complete the security check just now — that's on our side, not yours. " +
           "Please try again in a moment.",
  };
}

/** …as a Response, so a caller that only knows how to read `res.status` and `res.json()` works. */
export function turnstileUnavailableResponse() {
  return new Response(JSON.stringify(turnstileUnavailableBody()), {
    status: 429,
    headers: { "Content-Type": "application/json" },
  });
}

/**
 * What an anonymous request to a gated endpoint carries, and whether it may be sent at all.
 *
 * @param {string} url
 * @param {{signedIn?: boolean}} opts
 * @returns {Promise<{headers: Record<string,string>, refusal: Response|null, state: string}>}
 *
 * 🔴 A SIGNED-IN CALLER GETS NOTHING FROM HERE, AND THAT IS THE "NO CHALLENGE FOR A SIGNED-IN USER"
 * REQUIREMENT, ENFORCED WHERE IT CANNOT BE FORGOTTEN. `getTurnstileToken()` is never called, so the
 * Cloudflare script is never injected and no request is made to challenges.cloudflare.com at all —
 * not "a challenge that stays hidden", but no third party in the page. Their fingerprint is not
 * collected either: `x-cc-fp` exists to identify people who have not identified themselves, and
 * `anonTrial.identity()` is only ever consulted on the anonymous branch.
 */
export async function anonRequest(url, { signedIn = false } = {}) {
  const headers = {};
  if (signedIn || !isAnonGatedUrl(url)) return { headers, refusal: null, state: "n/a" };

  // Awaited, never fired-and-forgotten: a turn sent without the fingerprint is counted against a
  // DIFFERENT identity than the visitor's next one. It can never block — getFingerprint() resolves
  // null on any failure and times out on its own.
  let fp = null;
  try { fp = await getFingerprint(); } catch { /* fall back to cookie/IP, per identity() */ }
  if (fp) headers["x-cc-fp"] = fp;

  let ts;
  try { ts = await getTurnstileToken(); }
  catch { ts = { state: "unreachable", token: null }; }

  if (ts.state === "ok" && ts.token) {
    headers["cf-turnstile-response"] = ts.token;
    return { headers, refusal: null, state: "ok" };
  }
  if (ts.state === "unconfigured") {
    // State 1, the mirror of turnstile.js's one named fail-open. Send it unchallenged; the server
    // is either unconfigured too (and allows) or configured (and refuses with `missing_token`,
    // which is a real refusal the visitor can retry past once the key is deployed).
    return { headers, refusal: null, state: "unconfigured" };
  }
  return { headers, refusal: turnstileUnavailableResponse(), state: "unreachable" };
}

/**
 * The whole wrapper, for a caller that has no other headers to think about.
 * `baseHeaders` is merged UNDER the anonymous ones, so nothing can overwrite the token.
 */
export async function anonFetch(url, opts = {}, { signedIn = false, fetchImpl = null } = {}) {
  const pre = await anonRequest(url, { signedIn });
  if (pre.refusal) return pre.refusal;
  const doFetch = fetchImpl || fetch;
  return doFetch(url, { ...opts, headers: { ...(opts.headers || {}), ...pre.headers } });
}
